import { useContext, useState, useEffect } from "react";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIButton from "@mui/material/Button";
import MUIBox from "@mui/material/Box";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUICheckbox from "@mui/material/Checkbox";
import MUICloudUploadIcon from "@mui/icons-material/CloudUpload";
import MUIAddIcon from "@mui/icons-material/Add";
import MUIRequestQuoteIcon from "@mui/icons-material/RequestQuote";
import MUITypography from "@mui/material/Typography";
import MUIStack from "@mui/material/Stack";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIDescriptionIcon from '@mui/icons-material/Description';
import MUIAutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MUITextSnippetIcon from '@mui/icons-material/TextSnippet';
import MUIInsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MUIDeleteIcon from '@mui/icons-material/Delete';
import MUICloseIcon from '@mui/icons-material/Close';
import MUIChevronRightIcon from '@mui/icons-material/ChevronRight';
import MUIChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MUIGrid from "@mui/material/Grid";
import MUISearchIcon from '@mui/icons-material/Search';
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUIChip from "@mui/material/Chip";
import MUIAlert from "@mui/material/Alert";

import { ChartContext } from "../../../Context/ChartContext/ChartContext";

import Menu from "../../Menu";
import Card from "../../Card";
import DialogConfirm from "../../Dialog/DialogConfirm";
import EmptyState from "../../EmptyState";
import TileUploadedFile from "../../Tile/TileUploadedFile";
import Setting from "../../../Utility/Setting";
import Security from "../../../Utility/Security";
import UploadedFile from "../../../Utility/Crud/UploadedFile";
import api from "../../../Seating/Security/api";
import Vendor from "../../../Utility/Crud/Vendor";
import User from "../../../Utility/Crud/User";
import DialogUploadQuote from "../../Dialog/DialogUploadQuote";
import PdfMergeEditor from "../../PdfMergeEditor";
import Patient from "../../../Utility/Crud/Patient";

import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import Product from "../../../Utility/Crud/Product";
import ActivityFileType from "../../../Utility/Crud/ActivityFileType";
import File from "../../../Utility/File";

/**
 * CardChartAttachments renders a Card component
 * displaying attachments for an order.
 * @returns {JSX.Element}
 */

const title = "Attachments";
const icon = MUIInsertDriveFileIcon;
const className = "CardChartAttachments";

export default function CardChartAttachments(props) {
    const unrestrictedActivityFileTypeTermIds = [142, 145, 151]; // Appointment of Representative, Completed PPW, and Quote
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);

    const patient = chartContext.chart.patient[chartContext.patientId];
    const order = chartContext.chart.orderActivity[chartContext.orderId];
    const salesLocation = mainContext.locationsIndexed?.[order?.salesLocationId];
    const serviceLocation = mainContext.locationsIndexed?.[order?.serviceLocationId];
    const showAiQuoteParserCheckbox = props.showAiQuoteParserCheckbox !== undefined ? props.showAiQuoteParserCheckbox : true;
    /**
     * Normalizes an organization name for comparison.
     * @param {string} organizationName The organization name.
     * @returns {string} The normalized organization name.
     */
    const getNormalizedOrganizationName = (organizationName) => {
        return organizationName.toLowerCase().replace(/[^a-z0-9]/g, "");
    };
    const organization = Object.values(mainContext.organizationsIndexed).find((candidateOrganization) =>
        getNormalizedOrganizationName(candidateOrganization.name) === getNormalizedOrganizationName(mainContext.companyName)
    );
    const inboundFilesTestingLocationIds = organization?.jsonConfiguration?.inboundFilesTestingLocationIds;
    const isInboundFilesTestingLocation = Array.isArray(inboundFilesTestingLocationIds) &&
        inboundFilesTestingLocationIds.includes(order.salesLocationId);
    const canUploadAllActivityFileTypes = !isInboundFilesTestingLocation ||
        Security.hasPermission("order.uploadAllDocumentTypes");

    const isDeleteRestrictedState = (value) => {
        return typeof value === "string" && value.trim().toLowerCase() === "tx";
    };

    const disableDeleteForStateRestriction =
        !Security.hasPermission("order.deleteAnyFile") && (
            isDeleteRestrictedState(patient?.state) ||
            isDeleteRestrictedState(salesLocation?.state) ||
            isDeleteRestrictedState(serviceLocation?.state)
        );

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedActivityFileType, setSelectedActivityFileType] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [documentName, setDocumentName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [addTimestamp, setAddTimestamp] = useState(true);
    const getDefaultUseAIAssist = () => showAiQuoteParserCheckbox && order?.orderType === "New" && !order?.isApproved;
    const [useAIAssist, setUseAIAssist] = useState(getDefaultUseAIAssist());
    const [editMode, setEditMode] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [uploadQuoteDialogOpen, setUploadQuoteDialogOpen] = useState(false);
    const [uploadQuoteData, setUploadQuoteData] = useState(null);
    const [uploadingQuote, setUploadingQuote] = useState(false);
    const [originalUploadQuoteFileId, setOriginalUploadQuoteFileId] = useState(null);
    const [addingOrderProducts, setAddingOrderProducts] = useState(false);
    const [shouldSave, setShouldSave] = useState(false);
    const [mergeFile, setMergeFile] = useState(null);
    const [uploadedQuoteError, setUploadedQuoteError] = useState(false);
    const [deleteUploadedFile, setDeleteUploadedFile] = useState(null);
    const [deletingFile, setDeletingFile] = useState(false);
    const [showMissingFileTypes, setShowMissingFileTypes] = useState(false);

    // Sync this card's edit mode with ChartContext for ChartNavigationBar display
    useEffect(() => {
        chartContext.setCardEditMode?.(className, editMode);
        return () => chartContext.setCardEditMode?.(className, false);
    }, [editMode, chartContext.setCardEditMode]);

    /**
     * Sets the useAIAssist state to false if the order is approved.
     */
    useEffect(() => {
        if (order?.isApproved) {
            setUseAIAssist(false);
        }
    }, [order?.isApproved]);

    useEffect(() => {
        // Only get uploadedFileIds where activityId matches current order.id
        const uploadedFileIds = Object.values(chartContext.chart.activityFile)
            .filter(activityFile => activityFile.activityId === order?.id)
            .map(activityFile => activityFile.fileId);

        // Build uploadedFiles list from uploadedFileIds, excluding deleted files
        const uploadedFiles = uploadedFileIds
            .map(fileId => {
                const uploadedFile = chartContext.chart.uploadedFile[fileId];
                if (!uploadedFile) {
                    return null;
                }

                // Check if the uploadedFile is deleted by checking the activityFile
                if (UploadedFile.getActivityFile(uploadedFile, chartContext.chart)?.deleted === true) {
                    return null;
                }

                return uploadedFile;
            })
            .filter(Boolean);

        setUploadedFiles(uploadedFiles);
    }, [chartContext.chart.uploadedFile, chartContext.originalChart]);

    /**
     * Sets the default document name for merged documents.
     */
    useEffect(() => {
        if (selectedActivityFileType?.value === 145) { // 145 = Completed PPW
            var fileName = `${patient.firstName}_${patient.lastName}-${selectedActivityFileType.label}`;
            setDocumentName(sanitizeFilename(fileName));
        } else {
            setDocumentName("");
        }
    }, [selectedActivityFileType]);

    /**
     * Handles the saving of the order.
     * @returns {Promise} A promise that resolves when the order is saved.
     */
    useEffect(() => {
        if (!shouldSave) {
            return;
        }

        chartContext.handleSaveChart(null, async () => {
            setAddingOrderProducts(false);
            setUploadQuoteDialogOpen(false);
            setSelectedFile(null);
            setDocumentName("");
            setSelectedActivityFileType(null);
            setSelectedVendor(null);
            setUseAIAssist(getDefaultUseAIAssist());
            setAddTimestamp(true);
            setEditMode(false);
            setShouldSave(false);
            setOriginalUploadQuoteFileId(null);
        })
    }, [shouldSave]);

    /**
     * Sets the uploaded quote error state based on the selected file and document type.
     */
    useEffect(() => {
        if (selectedFile && selectedActivityFileType?.value === 151 && !selectedFile[0].type.includes("pdf")) { // 151 = Quote
            setUploadedQuoteError(true);
        } else {
            setUploadedQuoteError(false);
        }
    }, [selectedFile]);

    /**
     * Sanitizes a filename by replacing all non-alphanumeric characters with underscores.
     * @param {string} filename - The filename to sanitize.
     * @returns {string} The sanitized filename.
     */
    const sanitizeFilename = (filename) => {
        return filename
            .toLowerCase()
            .replaceAll(/[^.a-z0-9_-]/gi, '_');
    }

    /**
     * Returns all purchase_queue rows associated with a specific quote file.
     * This is used to determine whether a quote has already been ordered and,
     * if so, what the most recent status is so the UI can reflect that state.
     *
     * @param {number} uploadedFileId - The ID of the quote file.
     * @returns {Array<Object>} An array of purchase_queue chart entries.
     */
    const getPurchaseQueuesForQuoteFile = (uploadedFileId) => {
        return Object.values(chartContext.chart.purchaseQueue || {})
            .filter(purchaseQueue =>
                purchaseQueue.quoteId === uploadedFileId &&
                purchaseQueue.orderId === chartContext.orderId &&
                !purchaseQueue.deleted
            );
    };

    /**
     * Builds the display label for the "Order from quote" menu option.
     *
     * If the quote has not been ordered, a simple label is shown.
     * If purchase_queue rows already exist, the label includes the
     * most recent status and date to provide user context.
     *
     * @param {Object} uploadedFile - The uploaded file object representing the quote.
     * @returns {string} The menu label to display.
     */
    const getOrderFromQuoteMenuLabel = (uploadedFile) => {
        const purchaseQueues = getPurchaseQueuesForQuoteFile(uploadedFile.id);

        if (!purchaseQueues.length) {
            return "Order from quote";
        }

        const mostRecentPurchaseQueue = purchaseQueues
            .sort((firstQueue, secondQueue) =>
                new Date(secondQueue.statusChangedOn) - new Date(firstQueue.statusChangedOn)
            )[0];

        if (!mostRecentPurchaseQueue.status) {
            return "Order from quote (Status Unknown)";
        }

        const formattedStatus =
            mostRecentPurchaseQueue.status.charAt(0).toUpperCase() +
            mostRecentPurchaseQueue.status.slice(1).toLowerCase();

        if (mostRecentPurchaseQueue.statusChangedOn) {
            const formattedDate = new Date(
                mostRecentPurchaseQueue.statusChangedOn
            ).toLocaleDateString();

            return `Order from quote (${formattedStatus} on ${formattedDate})`;
        }

        return `Order from quote (${formattedStatus})`;
    };

    /**
     * Creates a new purchase_queue chart entry for a given quote file.
     *
     * This replaces the legacy PurchaseQueueService.createPurchaseQueues call
     * and instead stages the row directly in chart state using a negative ID.
     * The row is persisted when the chart is saved.
     *
     * @param {Object} uploadedFile - The quote file being ordered from.
     */
    const createPurchaseQueueFromQuote = (uploadedFile) => {
        const negativeTemporaryId = chartContext.getNegativeId();
        const currentUser = mainContext.currentUser;
        const activityFile = UploadedFile.getActivityFile(uploadedFile, chartContext.chart);

        chartContext.onChange({
            purchaseQueue: {
                [negativeTemporaryId]: {
                    id: negativeTemporaryId,
                    orderId: chartContext.orderId,
                    quoteId: uploadedFile.id,
                    vendorId: activityFile?.vendorId ?? null,
                    quantity: 1,
                    status: "PENDING",
                    requestedOn: new Date().toISOString(),
                    requestedById: currentUser.id,
                    statusChangedOn: new Date().toISOString(),
                    statusChangedById: currentUser.id,
                }
            }
        });
    };

    /**
     * Handles the application of products to the order.
     * @param {Array} products - The products to apply to the order.
     * @param {boolean} overwriteExisting - Whether to overwrite existing
     * products for this order.
     * @returns {Promise} A promise that resolves when the products are applied.
     */
    const handleApplyProducts = async (products, overwriteExisting) => {
        setAddingOrderProducts(true);

        const productsWithProductSubType = products.filter(
            product => product?.productSubType && Object.keys(product.productSubType).length > 0
        );

        if (!productsWithProductSubType.length) {
            setAddingOrderProducts(false);
            return;
        }

        // Build the products and orderActivityProducts objects
        const productsObject = {};
        const orderActivityProductsObject = {};

        // Remove all products for the current order if overwriteExisting is
        // true
        if (overwriteExisting) {
            Object.values(chartContext.chart.orderActivityProducts)
                .filter(orderActivityProduct => orderActivityProduct.orderActivityId === chartContext.orderId)
                .forEach(orderActivityProduct => {
                    productsObject[orderActivityProduct.productsId] = { deleted: true };
                });
        }

        // Loop through the products and build the products and orderActivityProducts objects
        productsWithProductSubType.forEach((product, index) => {
            let negativeId = chartContext.getNegativeId();
            const quantity = product.qty || 1;
            productsObject[negativeId] = {
                id: negativeId,
                abnRequired: false,
                approved: false,
                hcpcs: product.productSubType.hcpcs,
                productSubTypeId: product.productSubType.id,
                productTypeId: product.productSubType.productTypeId,
                deliverable: true,
                billable: true,
                qty: quantity,
                points: Product.getPoints(product.productSubType, order.insuranceTypeId, mainContext.insuranceMatrixEntriesIndexed, mainContext.productMatrixEntriesIndexed),
                serial: "",
                amount: Product.getAmount(
                    product.productSubType.hcpcs,
                    false,
                    mainContext.hcpcsCodesIndexed,
                    mainContext.feeSchedulesIndexed,
                ) ?? product.msrp ?? null,
                isRental: false,
                upgradedFromProductId: null,
            };
            orderActivityProductsObject[negativeId] = {
                orderActivityId: chartContext.orderId,
                productsId: negativeId,
            };
        });

        chartContext.onChange({
            product: productsObject,
            orderActivityProducts: orderActivityProductsObject,
        });

        setShouldSave(true);
    }

    /**
     * Handles the reprocessing of a quote.
     * @param {string} quoteFileId - The ID of the quote file to reprocess.
     * @returns {Promise} A promise that resolves to the reprocessed quote data.
     */
    const handleReprocessQuote = async (quoteFileId) => {
        if (!originalUploadQuoteFileId && !quoteFileId) {
            NotistackEnqueueSnackbar("Issue reprocessing quote", { variant: "error" });
            return;
        }

        const uploadedFileId = quoteFileId ? quoteFileId : originalUploadQuoteFileId;

        setUploadingQuote(true);
        setUploadQuoteDialogOpen(true);
        setOriginalUploadQuoteFileId(uploadedFileId);

        return await api.send("POST", "/aiQuoteParser/sendToAiQuoteParser", { UploadedFileId: uploadedFileId })
            .then((res) => {
                setUploadQuoteData(res);
                setUploadingQuote(false);
            }).catch((e) => {
                NotistackEnqueueSnackbar("Reprocess failed.", {
                    variant: "error"
                });
                setUploadingQuote(false);
            });
    }

    /**
     * Deletes the selected uploaded file after user confirmation.
     * @returns {Promise<void>}
     */
    const handleDeleteUploadedFile = async () => {
        const activityFile = UploadedFile.getActivityFile(deleteUploadedFile, chartContext.chart);

        setDeletingFile(true);

        try {
            await chartContext.handleSaveChart(async () => {
                await api.send("DELETE", `/files/${activityFile.id}`);
            });
            setDeleteUploadedFile(null);
            NotistackEnqueueSnackbar("File deleted", { variant: "success" });
        } catch (e) {
            NotistackEnqueueSnackbar("Failed to delete file", { variant: "error" });
        } finally {
            setDeletingFile(false);
        }
    };


    /**
     * Renders the edit view displaying patient contact information.
     * @returns {JSX.Element} Grid layout with patient information display
     */
    const renderEditView = () => {
        const getActivityFileTypeOptions = () => {
            const activityFileTypes = Object.values(mainContext.termsIndexed)
                .filter(term => term.type === 'activity_file_type')
                .filter(activityFileType =>
                    canUploadAllActivityFileTypes || unrestrictedActivityFileTypeTermIds.includes(activityFileType.id)
                );

            return ActivityFileType.sort(activityFileTypes).map(activityFileType => ({
                label: activityFileType.name,
                value: activityFileType.id
            }));
        }

        const getVendorOptions = () => {
            return Vendor.sort(Object.values(mainContext.vendorsIndexed)).map(vendor => ({
                label: vendor.name,
                value: vendor.id
            }))
        }

        return (
            <MUIGrid container spacing={2} rowSpacing={0}>
                <MUIGrid item lg={4} xs={12}>
                    <MUIAutocomplete
                        options={getActivityFileTypeOptions()}
                        value={selectedActivityFileType}
                        onChange={(e, newValue) => {
                            setSelectedActivityFileType(newValue || null);
                            setSelectedVendor(null);

                        }}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        disableClearable
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                variant="standard"
                                label="Document Type"
                                required
                                fullWidth
                            />
                        )}
                    />
                </MUIGrid>
                {selectedActivityFileType?.value !== 149 && selectedActivityFileType?.value !== 151 && selectedActivityFileType?.value !== 145 ? null : ( // 149 = Other, 151 = Quote, 145 = Completed PPW
                    <MUIGrid item lg={4} xs={12}>
                        <MUITextField
                            size="small"
                            label="Document Name"
                            variant="standard"
                            fullWidth={true}
                            inputProps={{
                                maxLength: 255,
                            }}
                            value={documentName || ""}
                            sx={selectedActivityFileType?.value === 149 ? { mb: 2 } : {}} // 149 = Other
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => {
                                setDocumentName(e.target.value.trim());
                            }}
                            onChange={(e) => {
                                setDocumentName(e.target.value);
                            }}
                            required={selectedActivityFileType?.value === 149 || selectedActivityFileType?.value === 145} // 149 = Other, 145 = Completed PPW
                        />
                    </MUIGrid>
                )}
                {selectedActivityFileType?.value === 145 ? null : (
                    <MUIGrid item xs={12}>
                        <MUIFormControlLabel
                            control={
                                <MUICheckbox
                                    size="small"
                                    checked={addTimestamp}
                                    onChange={(e) => setAddTimestamp(e.target.checked)}
                                />
                            }
                            label="Add Timestamp - Include the current timestamp at the top of the file"
                        />
                    </MUIGrid>
                )}
                {!selectedActivityFileType || selectedActivityFileType.value !== 151 ? null : ( // 151 = Quote
                    <MUIGrid item lg={4} xs={12}>
                        <MUIAutocomplete
                            options={getVendorOptions()}
                            onChange={(e, newValue) => {
                                setSelectedVendor(newValue || null);
                            }}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Vendor"
                                    required
                                    fullWidth
                                />
                            )}
                        />
                    </MUIGrid>
                )}
                {selectedActivityFileType?.value === 151 && showAiQuoteParserCheckbox && order?.orderType === "New" ? ( // 151 = Quote
                    <MUIGrid item xs={12}>
                        <MUIFormControlLabel
                            control={
                                <MUICheckbox
                                    size="small"
                                    checked={useAIAssist}
                                    onChange={(e) =>
                                        setUseAIAssist(e.target.checked)}
                                    disabled={Boolean(order?.isApproved)}
                                />
                            }
                            label="AI Assist - Automatically suggest equipment from your quote"
                        />
                        {order?.isApproved === true ? (
                            <MUIAlert severity="info" sx={{ mt: 1, width: "100%" }}>
                                AI Assist is not available because this order has been approved.
                            </MUIAlert>
                        ) : null}
                    </MUIGrid>
                ) : null}
                {uploadedQuoteError && (
                    <MUITypography variant="body2" color="error" sx={{ ml: 2 }}>
                        * Error: Quote documents must be PDF files
                    </MUITypography>
                )}
                <MUIGrid item xs={12}>
                    {selectedActivityFileType?.value === 145 ? ( // 145 = Completed PPW
                        <PdfMergeEditor
                            items={UploadedFile.sort(uploadedFiles).filter(item => item.contentType?.toLowerCase().includes('pdf'))}
                            handlePdfViewer={(uploadedFile) => handlePdfViewer(uploadedFile)}
                            prepareFile={(file) => setMergeFile(file)}
                        />
                    ) : (
                        renderDragAndDropFileSelectionSection()
                    )}
                </MUIGrid>
            </MUIGrid>
        );
    }

    /**
     * Renders the drag and drop file selection section.
     * @returns {JSX.Element} Grid layout with drag and drop file selection section
     */
    const renderDragAndDropFileSelectionSection = () => {
        const handleFileSelect = (e) => {
            setSelectedFile(e.target.files);
        };
        return (
            <MUIBox
                sx={{
                    border: '2px dashed',
                    width: '100%',
                    borderColor: (theme) => theme.palette.primary.main,
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: (theme) => theme.palette.background.default,
                    '&:hover': {
                        cursor: !selectedActivityFileType || (selectedActivityFileType.value === 151 && !selectedVendor || selectedActivityFileType.value === 149 && !documentName) ? 'not-allowed' : 'pointer' // 151 = Quote (must have vendor), 149 = Other (must have document name)
                    },
                    opacity: !selectedActivityFileType || (selectedActivityFileType.value === 151 && !selectedVendor || selectedActivityFileType.value === 149 && !documentName) ? 0.5 : 1 // 151 = Quote (must have vendor), 149 = Other (must have document name)
                }}
                component="label"
                onDrop={(e) => {
                    e.preventDefault();
                    if (!selectedActivityFileType || (selectedActivityFileType.value === 151 && !selectedVendor || selectedActivityFileType.value === 149 && !documentName)) { // 151 = Quote (must have vendor), 149 = Other (must have document name)
                        return;
                    }
                    const file = e.dataTransfer.files[0];
                    handleFileSelect({ target: { files: [file] } });
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <input
                    type="file"
                    hidden
                    onChange={handleFileSelect}
                    disabled={!selectedActivityFileType || (selectedActivityFileType.value === 151 && !selectedVendor || selectedActivityFileType.value === 149 && !documentName)} // 151 = Quote (must have vendor), 149 = Other (must have document name)
                />
                <MUIStack spacing={1} alignItems="center">
                    <MUICloudUploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    {selectedFile ? (
                        <>
                            <MUITypography variant="h6" color="primary">
                                Selected file: {selectedFile[0].name}
                            </MUITypography>
                            <MUITypography variant="body2" color="text.secondary">
                                Size: {(selectedFile[0].size / 1024).toFixed(2)} KB
                            </MUITypography>
                        </>
                    ) : (
                        <>
                            <MUITypography variant="h6" color="primary">
                                Drag and drop file here
                            </MUITypography>
                            <MUITypography variant="body2" color="text.secondary">
                                or click to select file
                            </MUITypography>
                        </>
                    )}
                    <MUIStack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {selectedFile && (
                            <MUIButton
                                variant="outlined"
                                size="small"
                                color="error"
                                disabled={!selectedActivityFileType || (selectedActivityFileType.value === 151 && !selectedVendor || selectedActivityFileType.value === 149 && !documentName)} // 151 = Quote (must have vendor), 149 = Other (must have document name)
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedFile(null);
                                }}
                            >
                                Clear File
                            </MUIButton>
                        )}
                        <MUIButton
                            variant="outlined"
                            component="label"
                            size="small"
                            disabled={!selectedActivityFileType || (selectedActivityFileType.value === 151 && !selectedVendor || selectedActivityFileType.value === 149 && !documentName)} // 151 = Quote (must have vendor), 149 = Other (must have document name)
                        >
                            Browse File
                            <input
                                type="file"
                                hidden
                                onChange={handleFileSelect}
                            />
                        </MUIButton>
                    </MUIStack>
                </MUIStack>
            </MUIBox>
        )
    }

    /**
     * Renders the read-only view displaying patient demographic information.
     * @returns {JSX.Element} Grid layout with patient information display
     */
    const renderReadOnlyView = () => {
        const iconButtons = (uploadedFile) => {
            const activityFile = UploadedFile.getActivityFile(uploadedFile, chartContext.chart);
            const isQuote = activityFile?.activityFileTypeTermId === 151; // 151 = Quote
            const canReprocess = activityFile?.activityFileTypeTermId === 151 && showAiQuoteParserCheckbox && order?.orderType === "New"; // 151 = Quote
            const buttons = [
                (
                    <Menu
                        menuItems={(() => {
                            const menuItems = [];

                            if (canReprocess) {
                                menuItems.push(
                                    <MUIMenuItem
                                        onClick={() => {
                                            // Save changes before reprocessing quote to trigger validation rules or pending changes needing saved
                                            // TODO this needs to be reviewed by JZ - pending save changes dialog does not open anymore
                                            chartContext.handleSaveChart(async () => {
                                                handleReprocessQuote(uploadedFile.id)
                                            })
                                        }}
                                        disabled={!uploadedFile.contentType.includes("pdf") || order?.isApproved}
                                    >
                                        <MUIListItemIcon>
                                            <MUIAutoAwesomeIcon />
                                        </MUIListItemIcon>
                                        Reprocess
                                    </MUIMenuItem>
                                );
                            }

                            // Order from quote (quote-only)
                            if (isQuote && order?.orderType === "New") {
                                const purchaseQueuesForQuote = getPurchaseQueuesForQuoteFile(uploadedFile.id);
                                const hasExistingOrder = purchaseQueuesForQuote.length > 0;

                                menuItems.push(
                                    <MUIMenuItem
                                     key={`order-from-quote-${uploadedFile.id}`}
                                        disabled={hasExistingOrder}
                                        onClick={() => {
                                            createPurchaseQueueFromQuote(uploadedFile);
                                            setShouldSave(true);
                                        }}
                                    >
                                        <MUIListItemIcon>
                                            <MUIRequestQuoteIcon />
                                        </MUIListItemIcon>
                                        {getOrderFromQuoteMenuLabel(uploadedFile)}
                                    </MUIMenuItem>
                                );
                            }

                            menuItems.push(
                                <MUIMenuItem
                                    disabled={disableDeleteForStateRestriction}
                                    onClick={() => {
                                        setDeleteUploadedFile(uploadedFile);
                                    }}
                                >
                                    <MUIListItemIcon>
                                        <MUIDeleteIcon />
                                    </MUIListItemIcon>
                                    Delete
                                </MUIMenuItem>
                            );
                            
                            return menuItems;
                        })()}
                    />
                ),
            ];

            return buttons;
        };

        // Filter uploadedFiles by attachment name or user
        const trimmedSearchValue = searchValue.trim().toLowerCase();
        const filteredUploadedFiles = uploadedFiles.filter(uploadedFile => {
            const filenameWithSpaces = uploadedFile.filename.trim().toLowerCase().replace(/_/g, ' ');
            const uploadedByUser = uploadedFile.uploaderId ? User.getDisplayName(mainContext.usersIndexed[uploadedFile.uploaderId]) : null;
            return filenameWithSpaces.includes(trimmedSearchValue) ||
                uploadedByUser?.trim().toLowerCase().includes(trimmedSearchValue)
        });

        // Some of these are name instead of ID because they do some custom
        // stuff. This is mostly legacy and should be avoided if possible moving
        // forward.
        const fileTypeChips = [
            { name: "Chart Notes", uploadedFileId: Patient.getLastChartNotesUploadedFileId(chartContext.chart) },
            { name: "AoR", uploadedFileId: Patient.getLastAppointmentOfRepresentativeUploadedFileId(chartContext.chart) },
            { name: "Medicare Rx", uploadedFileId: Patient.getLastMedicareRxUploadedFileId(chartContext.chart) },
            { activityFileTypeTermId: 281, uploadedFileId: Patient.getLastUploadedFileIdByActivityFileType(chartContext.chart, 281) }, // 281 = Complaint Resolution Letter
            { activityFileTypeTermId: 282, uploadedFileId: Patient.getLastUploadedFileIdByActivityFileType(chartContext.chart, 282) }, // 282 = POA Authorization
            { activityFileTypeTermId: 283, uploadedFileId: Patient.getLastUploadedFileIdByActivityFileType(chartContext.chart, 283) }, // 283 = Release of Information
            { activityFileTypeTermId: 284, uploadedFileId: Patient.getLastUploadedFileIdByActivityFileType(chartContext.chart, 284) }, // 284 = Infestation Remediation
        ];

        // Uploaded types are always listed before missing ones so expanding
        // never pushes a missing type in front of an uploaded one.
        const uploadedFileTypeChips = fileTypeChips.filter(fileTypeChip => fileTypeChip.uploadedFileId);
        const missingFileTypeChips = fileTypeChips.filter(fileTypeChip => !fileTypeChip.uploadedFileId);
        const visibleFileTypeChips = showMissingFileTypes
            ? [...uploadedFileTypeChips, ...missingFileTypeChips]
            : uploadedFileTypeChips;

        return (
            <>
                <MUIBox sx={{ flexGrow: 1 }}>
                <MUIStack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                    {visibleFileTypeChips.map(fileTypeChip => (
                        <MUIChip
                            key={fileTypeChip.activityFileTypeTermId ?? fileTypeChip.name}
                            label={fileTypeChip.name ?? ActivityFileType.getDisplayName(mainContext.termsIndexed[fileTypeChip.activityFileTypeTermId])}
                            color="primary"
                            variant={fileTypeChip.uploadedFileId ? "filled" : "outlined"}
                            clickable={!!fileTypeChip.uploadedFileId}
                            disabled={!fileTypeChip.uploadedFileId}
                            size="small"
                            icon={fileTypeChip.uploadedFileId ? <MUITextSnippetIcon /> : <MUICloseIcon />}
                            onClick={() => {
                                if (fileTypeChip.uploadedFileId) {
                                    let sanitizedFileName = File.sanitizeUrlFilename(chartContext.chart.uploadedFile[fileTypeChip.uploadedFileId].filename);
                                    window.open(`/file/${fileTypeChip.uploadedFileId}/${sanitizedFileName}`, "_blank");
                                }
                            }}
                        />
                    ))}
                    {missingFileTypeChips.length > 0 && (
                        <MUIChip
                            color="primary"
                            variant="outlined"
                            clickable
                            size="small"
                            title={showMissingFileTypes ? "Hide missing document types" : "Show missing document types"}
                            label={showMissingFileTypes ? <MUIChevronLeftIcon fontSize="small" /> : <MUIChevronRightIcon fontSize="small" />}
                            onClick={() => setShowMissingFileTypes(!showMissingFileTypes)}
                            sx={{ "& .MuiChip-label": { display: "flex", alignItems: "center", px: 0.5 } }}
                        />
                    )}
                </MUIStack>
                {uploadedFiles.length > 0 && (
                    <MUITextField
                        placeholder="Type to search..."
                        hiddenLabel={true}
                        fullWidth={true}
                        variant="filled"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <MUIInputAdornment position="start">
                                    <MUISearchIcon />
                                </MUIInputAdornment>
                            ),
                            disableUnderline: true,
                            style: { borderRadius: 4 }
                        }}
                        onChange={e => setSearchValue(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                )}
                {filteredUploadedFiles.length === 0 ? (
                    <EmptyState
                        line1="No attachments found"
                        line2="Upload a file to get started"
                    />
                ) : (
                    <MUIGrid container spacing={2}>
                        {UploadedFile.sort(filteredUploadedFiles).map((uploadedFile, idx) => (
                            <MUIGrid
                                item
                                xs={12}
                                lg={4}
                                key={uploadedFile.id || idx}
                            >
                                <TileUploadedFile
                                    uploadedFile={uploadedFile}
                                    iconButtons={iconButtons(uploadedFile)}
                                    onClick={(e) => {
                                        handlePdfViewer(uploadedFile)
                                    }}
                                />
                            </MUIGrid>
                        ))}
                    </MUIGrid>
                )}
                </MUIBox>
                <DialogConfirm
                    header={"Delete attachment"}
                    text={deleteUploadedFile ? `Delete ${deleteUploadedFile.filename}? This action cannot be undone.` : "Delete this attachment? This action cannot be undone."}
                    open={!!deleteUploadedFile}
                    loading={deletingFile}
                    onConfirm={() => handleDeleteUploadedFile()}
                    onClose={() => {
                        if (!deletingFile) {
                            setDeleteUploadedFile(null);
                        }
                    }}
                />
            </>
        );
    }

    /**
      * Determines which view to render based on edit mode state.
      * @returns {JSX.Element} Either edit view or read-only view
      */
    const renderContent = () => {
        if (editMode) {
            return (
                renderEditView()
            )
        }
        return (
            renderReadOnlyView()
        );
    }

    /**
     * Handles the on upload quote event.
     * @param {Object} data - The data to set.
     * @param {boolean} uploadingQuote - Whether the quote is being uploaded.
     */
    const handleOnUploadQuote = (data, uploadingQuote) => {
        setUploadingQuote(uploadingQuote);
        setUploadQuoteData(data);
        setUploadQuoteDialogOpen(true);
        setOriginalUploadQuoteFileId(data?.uploadedFileId ?? null);
    }

    /**
     * Handles the pdf viewer.
     * @param {Object} uploadedFile - The uploaded file to handle.
     */
    const handlePdfViewer = (uploadedFile) => {
        if (!Security.hasPermission("file.download")) {
            Setting.set("jspdf.file.download", false);
        } else {
            Setting.set("jspdf.file.download", true);
        }
        let sanitizedFileName = File.sanitizeUrlFilename(uploadedFile.filename);
        window.open(`/file/${uploadedFile.id}/${sanitizedFileName}`, "_blank");
    }

    /**
     * Opens the generated AoR PDF in the file viewer.
     * @returns {Promise<void>}
     */
    const handlePreviewAor = async () => {
        if (!Security.hasPermission("file.download")) {
            Setting.set("jspdf.file.download", false);
        } else {
            Setting.set("jspdf.file.download", true);
        }

        try {
            const viewerOpened = await File.openViewer(
                `${patient.firstName}-${patient.lastName}-Appointment-of-Representative.pdf`,
                async () => {
                    const pdf = await api.send("GET", `/deliveryPaperwork/aorPDF/${order.id}`);
                    return new Blob([pdf], { type: "application/pdf" });
                }
            );

            if (!viewerOpened) {
                NotistackEnqueueSnackbar("Allow pop-ups to preview the AoR", { variant: "warning" });
            }
        } catch (error) {
            NotistackEnqueueSnackbar("Failed to preview AoR", { variant: "error" });
        }
    }

    /**
     * Returns the action buttons for the card component.
     * @returns {Array} Array of action button components
     */
    const getActions = () => {
        return [
            <MUIBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {editMode && (
                    <MUIButton
                        color="error"
                        size="small"
                        onClick={() => {
                            chartContext.setCardEditMode?.(className, false);
                            setEditMode(false);
                            setSelectedFile(null);
                            setSelectedActivityFileType(null);
                            setSelectedVendor(null);
                            setDocumentName("");
                            setAddTimestamp(true);
                            setUseAIAssist(getDefaultUseAIAssist());
                        }}
                    >
                        Cancel
                    </MUIButton>
                )}
                <MUIButton
                    variant={editMode ? "contained" : "outlined"}
                    startIcon={editMode ? <MUICloudUploadIcon /> : <MUIAddIcon />}
                    disabled={(editMode && !selectedFile && !mergeFile) || uploadedQuoteError}
                    size="small"
                    onClick={async () => {
                        if (!editMode) {
                            setEditMode(true);
                            return;
                        }
                        if (selectedActivityFileType.value === 142) { // Appointment of Representative
                            chartContext.onChange({ patient: { [chartContext.patientId]: { lastAorDate: new Date().toISOString() } } })
                        }
                        await chartContext.handleSaveChart(async () => {
                            const sanitizedFilename = sanitizeFilename(documentName);
                            if (selectedActivityFileType.value === 145) { // 145 = Completed PPW
                                return api.send("POST", `/activities/${parseInt(order.id)}/mergepages/${sanitizedFilename}/${parseInt(mainContext.currentUser.id)}`, mergeFile)
                                    .then((res) => {
                                        setEditMode(false);
                                        setMergeFile(null);
                                        setSelectedActivityFileType(null);
                                        setDocumentName("");
                                        setAddTimestamp(true);
                                        setUseAIAssist(getDefaultUseAIAssist());
                                        NotistackEnqueueSnackbar("Documents merged successfully", { variant: "success" });
                                    })
                                    .catch((err) => {
                                        console.log(err);
                                        NotistackEnqueueSnackbar("Failed to merge documents", { variant: "error" });
                                    });
                            }
                            try {
                                const file = selectedFile?.[0];
                                const ext = `.${file.name.split('.').pop()}`;

                                // Build filename
                                let fileName = "";
                                if (selectedActivityFileType.value === 149) { // 149 = Other
                                    fileName = `${patient.firstName}_${patient.lastName}-${documentName}${ext}`;
                                } else if (selectedActivityFileType.value === 151) { // 151 = Quote
                                    if (documentName) {
                                        fileName = `${patient.firstName}_${patient.lastName}-${selectedVendor.label}-${documentName}-quote${ext}`;
                                    } else {
                                        fileName = `${patient.firstName}_${patient.lastName}-${selectedVendor.label}${ext}`;
                                    }
                                } else {
                                    fileName = `${patient.firstName}-${patient.lastName}-${selectedActivityFileType.label}${ext}`;
                                }

                                const sanitizedFilename = sanitizeFilename(fileName);

                                const sendToAi = order?.orderType === "New" &&
                                    useAIAssist &&
                                    selectedActivityFileType.label.toLowerCase().includes("quote");

                                const formData = new FormData();
                                formData.append("activityId", parseInt(order.id));
                                formData.append("filename", sanitizedFilename);
                                formData.append("userId", parseInt(mainContext.currentUser.id));
                                formData.append("file", file);
                                formData.append("activityFileTypeTermId", selectedActivityFileType.value);
                                formData.append("sendToAiQuoteParser", sendToAi);
                                formData.append("addTimestamp", addTimestamp);
                                if (selectedVendor) {
                                    formData.append("vendorId", selectedVendor.value);
                                }

                                let uploadPromise;
                                uploadPromise = api.send("POST", "/activities/uploadFile", formData);

                                if (sendToAi) {
                                    handleOnUploadQuote({}, true);
                                }

                                return uploadPromise
                                    .then((res) => {
                                        if (sendToAi) {
                                            const parsed = JSON.parse(res.aiResponse);
                                            setOriginalUploadQuoteFileId(res?.uploadedFileId ?? null);
                                            handleOnUploadQuote(parsed, false);
                                        } else {
                                            setEditMode(false);
                                            setSelectedFile(null);
                                            setSelectedActivityFileType(null);
                                            setSelectedVendor(null);
                                            setDocumentName("");
                                            setAddTimestamp(true);
                                            setUseAIAssist(getDefaultUseAIAssist());
                                            setOriginalUploadQuoteFileId(null);
                                            NotistackEnqueueSnackbar("File successfully uploaded", {
                                                variant: "success"
                                            });
                                        }
                                    }).catch((e) => {
                                        if (sendToAi) {
                                            handleOnUploadQuote(null, false);

                                        }
                                        NotistackEnqueueSnackbar("Failed to upload file", {
                                            variant: "error"
                                        });
                                        throw e;
                                    });
                            } catch (error) {
                                NotistackEnqueueSnackbar("Failed to upload file", { variant: "error" });
                                throw error;
                            }
                        });
                    }}
                >
                    Upload
                </MUIButton>
                <Menu
                    menuItems={[
                        <MUIMenuItem onClick={handlePreviewAor}>
                            <MUIListItemIcon>
                                <MUIDescriptionIcon fontSize="small" />
                            </MUIListItemIcon>
                            Generate AoR
                        </MUIMenuItem>
                    ]}
                />
                <DialogUploadQuote
                    open={uploadQuoteDialogOpen}
                    onClose={() => {
                        setUploadQuoteDialogOpen(false);
                        setUploadQuoteData(null);
                    }}
                    uploadQuoteData={uploadQuoteData}
                    addingProducts={addingOrderProducts}
                    isLoading={uploadingQuote}
                    onConfirm={(products, overwriteExisting) => handleApplyProducts(products, overwriteExisting)}
                    onReprocess={() => handleReprocessQuote()}
                />
            </MUIBox >
        ];
    }

    return (
        <Card
            renderContent={renderContent}
            getActions={getActions}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartAttachments.meta = { className: className, title: title, icon: icon };
